import type { MatrixClient, Room, MatrixEvent, User } from 'matrix-js-sdk'
import { RoomEvent, RoomMemberEvent, ClientEvent, MatrixEventEvent, NotificationCountType, UserEvent } from 'matrix-js-sdk'
import { useRoomStore } from '../store/room-store'
import { useTimelineStore } from '../store/timeline-store'
import { useMemberStore } from '../store/member-store'
import { useReceiptStore } from '../store/receipt-store'
import { useConnectionStore } from '../store/connection-store'
import { useThreadStore } from '../store/thread-store'
import { serializeEvent, type SerializedEvent } from './events'
import { getDirectUserId, detectBridge, isBridgeBot, NOT_DIRECT_EVENT, cleanupDirectSelfKeys } from './dm'

// Bridges (mautrix) create portal rooms and INVITE us to them — they don't add us
// directly. Auto-accept invites from bridge bots so e.g. Signal chats appear
// without manual joining (Element does this too).
function autoJoinBridgeInvite(client: MatrixClient, room: Room) {
  if (room.getMyMembership() !== 'invite') return
  const me = client.getUserId()
  const inviter = me
    ? room.currentState.getStateEvents('m.room.member', me)?.getSender()
    : undefined
  if (inviter && isBridgeBot(inviter)) {
    client.joinRoom(room.roomId).catch((err) => console.warn('Auto-join failed:', room.roomId, err))
  }
}

// Route a thread reply into the thread store instead of the main room timeline.
// Returns true if the event was a thread reply and has been handled — callers
// then skip the main-timeline path entirely, keeping replies out of the room.
// Thread roots are NOT diverted: they still render in the main timeline, but we
// seed their metadata so the global Threads list has a preview.
function routeThreadEvent(client: MatrixClient, serialized: SerializedEvent): boolean {
  const threadStore = useThreadStore.getState()
  if (serialized.isThreadRoot) {
    threadStore.seedThreadRoot(serialized)
    // fall through: a root is a normal room message too.
  }
  if (serialized.threadRootId) {
    const isOwn = serialized.sender === client.getUserId()
    threadStore.addThreadReply(serialized, isOwn)
    return true
  }
  return false
}

// Whether the room holds messages we haven't read — independent of push rules,
// so muted / notify-off rooms (notification count 0) still flag as unread. Looks
// at the newest message: unread if it isn't ours and we haven't read it.
function hasUnreadMessages(client: MatrixClient, room: Room): boolean {
  const me = client.getUserId()
  if (!me) return false
  const events = room.getLiveTimeline().getEvents()
  for (let i = events.length - 1; i >= 0; i--) {
    const event = events[i]!
    const type = event.getType()
    if (type !== 'm.room.message' && type !== 'm.room.encrypted' && type !== 'm.sticker') continue
    if (event.getSender() === me) return false
    const id = event.getId()
    return id ? !room.hasUserReadEvent(me, id) : false
  }
  return false
}

function roomToSummary(client: MatrixClient, room: Room) {
  const lastEvent = [...room.timeline].reverse().find(
    (e) => e.getType() === 'm.room.message' || e.getType() === 'm.room.encrypted' || e.getType() === 'm.sticker'
  ) ?? room.timeline[room.timeline.length - 1]
  const dmUserId = getDirectUserId(client, room)
  // DM rooms rarely carry a room avatar — fall back to the other party's avatar.
  const avatarUrl =
    room.getMxcAvatarUrl() ??
    (dmUserId ? room.getMember(dmUserId)?.getMxcAvatarUrl() ?? null : null)

  return {
    roomId: room.roomId,
    name: room.name || room.roomId,
    avatarUrl,
    topic: room.currentState.getStateEvents('m.room.topic', '')?.getContent()?.topic ?? '',
    isEncrypted: room.hasEncryptionStateEvent(),
    isDirect: !!dmUserId,
    bridgeType: detectBridge(room),
    lastEvent: lastEvent
      ? {
          body: lastEvent.getContent()?.body ?? '',
          msgtype: lastEvent.getContent()?.msgtype ?? '',
          type: lastEvent.getType(),
          sender: lastEvent.getSender() ?? '',
          timestamp: lastEvent.getTs(),
        }
      : null,
    unreadCount: room.getUnreadNotificationCount(NotificationCountType.Total) ?? 0,
    highlightCount: room.getUnreadNotificationCount(NotificationCountType.Highlight) ?? 0,
    hasUnread: hasUnreadMessages(client, room),
    membership: room.getMyMembership() as 'join' | 'invite' | 'leave',
  }
}

function watchForDecryption(client: MatrixClient, event: MatrixEvent, roomId: string) {
  const timelineStore = useTimelineStore.getState()
  const roomStore = useRoomStore.getState()

  if (event.isBeingDecrypted() || event.getType() === 'm.room.encrypted') {
    event.once(MatrixEventEvent.Decrypted, () => {
      const serialized = serializeEvent(event)
      // A decrypted thread reply belongs to the thread store, not the timeline.
      if (routeThreadEvent(client, serialized)) return
      timelineStore.addEvent(roomId, serialized)
      const currentLastTs = useRoomStore.getState().rooms.get(roomId)?.lastEvent?.timestamp ?? 0
      const isNewer = event.getTs() >= currentLastTs
      if (isNewer) {
        roomStore.upsertRoom(roomId, {
          lastEvent: {
            body: event.getContent()?.body ?? '',
            msgtype: event.getContent()?.msgtype ?? '',
            type: event.getType(),
            sender: event.getSender() ?? '',
            timestamp: event.getTs(),
          },
        })
      }
    })
  }
}

export function bindClientToStores(client: MatrixClient) {
  const roomStore = useRoomStore.getState()
  const timelineStore = useTimelineStore.getState()
  const memberStore = useMemberStore.getState()

  client.on(ClientEvent.Room, (room: Room) => {
    autoJoinBridgeInvite(client, room)
    roomStore.upsertRoom(room.roomId, roomToSummary(client, room))
  })

  // Re-evaluate which rooms are DMs whenever the direct-message mapping or our
  // manual override changes.
  client.on(ClientEvent.AccountData, (event: MatrixEvent) => {
    const type = event.getType()
    if (type !== 'm.direct' && type !== NOT_DIRECT_EVENT) return
    for (const room of client.getRooms()) {
      roomStore.upsertRoom(room.roomId, roomToSummary(client, room))
    }
  })

  client.on(RoomEvent.Timeline, (event: MatrixEvent, room: Room | undefined) => {
    if (!room) return
    const serialized = serializeEvent(event)
    // Thread replies are routed to the thread store and kept out of the main
    // room timeline (and out of the room's lastEvent / unread bump).
    if (routeThreadEvent(client, serialized)) {
      watchForDecryption(client, event, room.roomId)
      return
    }
    timelineStore.addEvent(room.roomId, serialized)
    const evType = event.getType()
    const isMessage = evType === 'm.room.message' || evType === 'm.room.encrypted' || evType === 'm.sticker'
    const currentLastTs = useRoomStore.getState().rooms.get(room.roomId)?.lastEvent?.timestamp ?? 0
    const isNewer = isMessage && event.getTs() >= currentLastTs
    const isActiveRoom = useRoomStore.getState().activeRoomId === room.roomId
    roomStore.upsertRoom(room.roomId, {
      ...(isNewer ? { lastEvent: {
        body: event.getContent()?.body ?? '',
        msgtype: event.getContent()?.msgtype ?? '',
        type: event.getType(),
        sender: event.getSender() ?? '',
        timestamp: event.getTs(),
      }} : {}),
      ...(isActiveRoom ? { hasUnread: false } : {
        unreadCount: room.getUnreadNotificationCount(NotificationCountType.Total) ?? 0,
        highlightCount: room.getUnreadNotificationCount(NotificationCountType.Highlight) ?? 0,
        hasUnread: hasUnreadMessages(client, room),
      }),
    })
    if (isActiveRoom) {
      import('../matrix/receipts').then(({ markRoomAsRead }) => markRoomAsRead(room.roomId))
    }
    watchForDecryption(client, event, room.roomId)
  })

  client.on(RoomEvent.LocalEchoUpdated, (event: MatrixEvent, room: Room, oldEventId?: string) => {
    const serialized = serializeEvent(event)
    // Keep local echoes of our own thread replies in the thread store.
    if (serialized.threadRootId) {
      if (oldEventId) useThreadStore.getState().removeThreadEvent(serialized.threadRootId, oldEventId)
      useThreadStore.getState().addThreadReply(serialized, true)
      return
    }
    if (oldEventId) {
      timelineStore.removeEvent(room.roomId, oldEventId)
    }
    timelineStore.addEvent(room.roomId, serialized)
  })

  client.on(RoomEvent.Name, (room: Room) => {
    roomStore.upsertRoom(room.roomId, { name: room.name })
  })

  client.on(RoomMemberEvent.Typing, (event: MatrixEvent, member: { roomId: string }) => {
    memberStore.setTyping(member.roomId, event.getContent()?.user_ids ?? [])
  })

  // Presence (online/away/offline) — the store + UI already exist; just feed them.
  client.on(UserEvent.Presence, (_event: MatrixEvent | undefined, user: User) => {
    memberStore.updatePresence(user.userId, {
      presence: (user.presence as 'online' | 'offline' | 'unavailable') ?? 'offline',
      lastActiveAgo: user.lastActiveAgo ?? undefined,
      statusMsg: user.presenceStatusMsg ?? undefined,
    })
  })

  // Read receipts — bump a per-room version so receipt indicators recompute from
  // the live Room object (which matrix-js-sdk keeps up to date).
  client.on(RoomEvent.Receipt, (_event: MatrixEvent, room: Room) => {
    useReceiptStore.getState().bump(room.roomId)
    // Our own read receipt (sent on open / scroll) should clear the unread state.
    const isActiveRoom = useRoomStore.getState().activeRoomId === room.roomId
    roomStore.upsertRoom(room.roomId, {
      unreadCount: room.getUnreadNotificationCount(NotificationCountType.Total) ?? 0,
      highlightCount: room.getUnreadNotificationCount(NotificationCountType.Highlight) ?? 0,
      hasUnread: isActiveRoom ? false : hasUnreadMessages(client, room),
    })
  })

  // Debug helper: `__cpDumpEvent()` prints the raw content of the last message in
  // the open room (and `__cpDumpEvent(n)` the nth-from-last) — used to inspect how
  // bridges represent forwards, contacts, etc.
  if (typeof window !== 'undefined') {
    ;(window as unknown as { __cpDumpEvent: (n?: number) => void }).__cpDumpEvent = (n = 0) => {
      const roomId = useRoomStore.getState().activeRoomId
      const room = roomId ? client.getRoom(roomId) : null
      if (!room) return console.log('[dump] no active room')
      const msgs = room.getLiveTimeline().getEvents().filter((e) => e.getType() === 'm.room.message' || e.getType() === 'm.sticker')
      const ev = msgs[msgs.length - 1 - n]
      if (!ev) return console.log('[dump] no message at that offset')
      console.log('[dump] type:', ev.getType(), '| sender:', ev.getSender())
      console.log('[dump] content:', JSON.stringify(ev.getContent(), null, 2))
    }
  }

  client.on(RoomMemberEvent.Membership, (_event: MatrixEvent, member: { roomId: string }) => {
    const room = client.getRoom(member.roomId)
    if (!room) return
    roomStore.upsertRoom(room.roomId, {
      membership: room.getMyMembership() as 'join' | 'invite' | 'leave',
    })
  })

  client.on(ClientEvent.Sync, (state: string) => {
    const connStore = useConnectionStore.getState()
    if (state === 'SYNCING' || state === 'PREPARED') {
      connStore.setSyncState('connected')
    } else if (state === 'RECONNECTING') {
      connStore.setSyncState('reconnecting')
    } else if (state === 'ERROR' || state === 'STOPPED') {
      connStore.setSyncState('offline')
    }

    if (state === 'PREPARED') {
      // Repair any self-keyed m.direct entries now that rooms + account data are
      // loaded; the resulting write re-renders affected DMs via the AccountData
      // handler above, so no reload is needed.
      cleanupDirectSelfKeys(client).catch((e) => console.warn('m.direct self-key cleanup failed', e))

      const rooms = client.getRooms()
      for (const room of rooms) {
        autoJoinBridgeInvite(client, room)
        roomStore.upsertRoom(room.roomId, roomToSummary(client, room))

        const timeline = room.getLiveTimeline().getEvents()
        for (const event of timeline) {
          const serialized = serializeEvent(event)
          // Backfilled thread replies go to the thread store, not the timeline.
          if (routeThreadEvent(client, serialized)) {
            watchForDecryption(client, event, room.roomId)
            continue
          }
          timelineStore.addEvent(room.roomId, serialized)
          watchForDecryption(client, event, room.roomId)
        }
      }
    }
  })
}
